import React, { createContext, useMemo } from 'react';
import { useSafeContext, createNamedContext } from '@clover-util/react-context';
import { CollectionDispatch, useSessionStorageCollection } from 'hooks/useCollection';
import { useMerchant } from '../merchant';
import { GiftCardItem } from 'olo-service/models';

const GiftCardCartDispatchContext = createNamedContext<CollectionDispatch<GiftCardItem>>('GiftCardCartDispatchContext');

export function useGiftCardCartDispatch() {
  return useSafeContext(useGiftCardCartDispatch, GiftCardCartDispatchContext);
}

type GiftCardCartState = {
  items: GiftCardItem[];
  itemCount: number;
  subtotal: number;
  isValid: boolean;
};
const GiftCardCartContext = createContext<GiftCardCartState | undefined>(undefined);
// eslint-disable-next-line functional/immutable-data
GiftCardCartContext.displayName = 'GiftCardCartContext';

export function useGiftCardCart() {
  return {
    ...useGiftCardCartDispatch(),
    ...useSafeContext(useGiftCardCart, GiftCardCartContext),
  };
}

export const GiftCardCartProvider: React.FC = ({ children }) => {
  const { merchant } = useMerchant();

  const merchantId = merchant?.merchantUuid;

  const [items, collection] = useSessionStorageCollection<GiftCardItem>((merchantId && `colo-gift-cards_${merchantId}`) || undefined);

  const dispatch = useMemo(() => ({
    ...collection,
    add: (...giftCardItems: GiftCardItem[]) => collection.add(...giftCardItems.filter(item => item.amount > 0)),
  }), [collection]);

  const itemCount = items.length;
  const subtotal = items.reduce((sum, item) => sum + item.amount, 0);

  return (
    <GiftCardCartDispatchContext.Provider value={dispatch}>
      <GiftCardCartContext.Provider
        value={{
          items,
          itemCount,
          subtotal,
          isValid: items.length > 0 && subtotal > 0,
        }}
      >
        {children}
      </GiftCardCartContext.Provider>
    </GiftCardCartDispatchContext.Provider>
  );
};
